import { isEmpty, isLength, isNumeric } from 'validator'
import {
  australianStates,
  isInternationalStudent,
  isInternationalOnshoreStudent,
  isDomesticStudent
} from './form'
import Country from '../enums/Country'

const addressInputValidationRegex = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ0-9][A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ0-9\s.,\-'/#&]*[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ0-9]?$/;

const validCharacters = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ \-']+$/;
const validEdges = /^[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ](?:.*[A-Za-zÀ-ÖØ-öø-ÿĀ-žḀ-ỿ])?$/;

export const normalizeWhitespace = (input) => input.trim().replace(/\s{2,}/g, ' ');

export const isValidEdgesCharacter = input => {
  if (!input) {
    return true
  }
  return validEdges.test(normalizeWhitespace(input))
}

export const isValidCharacters = input => {
  if (!input) {
    return true
  }
  return validCharacters.test(normalizeWhitespace(input))
}

export const isValidAddressCharacters = input => {
  if (!input) {
    return true
  }
  return addressInputValidationRegex.test(normalizeWhitespace(input))
}

export const isValidYear = input => {
  return isValidNumber(input) && hasCharCountEqualTo(input, 4)
}

export const isValidYearOfArrival = (input, context ) => {
  let form = context.parent
  
  if (!input) {
    return true
  } else {
    return !!input && isValidNumber(input) && hasCharCountEqualTo(input, 4) && isYearInRange(new Date(form.dateOfBirth).getFullYear(), new Date().getFullYear(), input)
  }
}

export const isValidYearOfLeavingSchool = (input, context ) => {
  let form = context.parent
  return isValidNumber(input) && hasCharCountEqualTo(input, 4) && isYearInRange(new Date(form.dateOfBirth).getFullYear() + 5, new Date().getFullYear(), input)
}

export const isYearInRange = (from, to, year) => from <= year && year <= to

export const isNotEmpty = input => {
  return !!input && !isEmpty(input)
}

export const hasCharCountLessThanOrEqualTo = (inputValue, length) => {
  return (isNotEmpty(inputValue) || typeof inputValue === 'string') &&
    isLength(inputValue, {
      min: 0,
      max: length
    })
}

export const hasCharCountEqualTo = (input, length) => {
  return !!input &&
    isLength(input, {
      min: length,
      max: length
    })
}
export const isVisaQuestionNotSelected = (input,  context ) => !!input && !(context.visaQuestionResponse === '')

export const isDisabilityNotSelected = (input) => !!input && !(input === '9')

export const isValidUSI = (input) => !!input && input.match(/^[2-9A-HJ-NP-Z]+$/)

export const isNotPOBox = input => !input.match(/\bbox(?:\b$|([\s|\-]+)?[0-9]+)|(p[\-\.\s]?o.?[\-\s]?|post office\s)b(\.|ox)?/gim)

export const isValidEmail = input => /^[A-Za-z0-9]+([._+-]?[A-Za-z0-9]+)*@[A-Za-z0-9]+([.\-]?[A-Za-z0-9]+)*\.[A-Za-z]{2,}$/.test(input)

export const isValidEmailDomain = input => !input.match(/^[a-zA-Z0-9._%+-]+@(students\.vu\.edu\.au|live\.vu\.edu\.au|vu\.edu\.au)$/i)

export const isValidNumber = input => !!input && isNumeric(input)

export const isValidResidencyPostcodeYear12 = (input, context) => {
  let form = context.parent

  if (form.year12InAustralia && form.year12InAustralia === 'Y') {
    return isValidNumber(input) && hasCharCountEqualTo(input, 4)
  }
  return true
}

export const isValidPermanentAustralianPostcode = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry === Country.Australia) {
    return isValidAustralianPostcode(input)
  }

  return true
}

export const isValidPermanentInternationalPostcode = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry !== Country.Australia) {
    return isValidInternationalPostcode(input)
  }

  return true
}

export const isValidContactAustralianPostcode = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry === Country.Australia) {
    return isValidAustralianPostcode(input)
  }

  return true
}

export const isValidContactInternationalPostcode = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry !== Country.Australia) {
    return isValidInternationalPostcode(input)
  }

  return true
}

const isValidAustralianPostcode = input => isValidNumber(input) && hasCharCountEqualTo(input, 4)

const isValidInternationalPostcode = input => hasCharCountLessThanOrEqualTo(input, 10)

export const isValidPermanentAustralianState = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry === Country.Australia) {
    return isValidAustralianState(input)
  }

  return true
}

export const isValidPermanentInternationalState = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry !== Country.Australia) {
    return isValidInternationalState(input)
  }

  return true
}

export const isValidContactAustralianState = (input, context) => {
  let form = context.parent;
  
  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry === Country.Australia) {
    return isValidAustralianState(input)
  }

  return true
}

export const isValidContactInternationalState = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry !== Country.Australia) {
    return isValidInternationalState(input)
  }

  return true
}

const isValidAustralianState = input => {
  return isNotEmpty(input) && australianStates.some(state => state.value === input)
}

const isValidInternationalState = input => {
  return hasCharCountLessThanOrEqualTo(input, 40)
}

export const isNotEmptyArray = input => !isEmptyArray(input)

export const isEmptyArray = input => input.length === 0

export const containNumerics = input => {
  return input && !!input.match(/\d/)
}

export const doesNotContainNumerics = input => !containNumerics(input)

export const isValidAustralianMobileNumber = (input, context) => {
  let form = context.parent
  const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)


  if (!internationalOnshoreStudent) {
    if (
      (form.contactAddressSameAsPermanentAddress && form.permanentCountry === Country.Australia) ||
      (!form.contactAddressSameAsPermanentAddress && form.contactCountry === Country.Australia)
    ) {
      return hasCharCountEqualTo(input, 10) && isValidPhoneNumber(input)
    }
  } else {
    if ((form.knowsAustralianAddress === 1 && form.contactCountry === Country.Australia) || (form.knowsAustralianAddress === 0 && form.permanentCountry === Country.Australia)) {
      return hasCharCountEqualTo(input, 10) && isValidPhoneNumber(input)
    }
  }

  return true
}

export const isValidInternationalMobileNumber = (input, context) => {
  let form = context.parent;
  const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)

  if (!internationalOnshoreStudent) {
    if (
      (form.contactAddressSameAsPermanentAddress && form.permanentCountry !== Country.Australia) ||
      (!form.contactAddressSameAsPermanentAddress && form.contactCountry !== Country.Australia)
    ) {
      return isValidInternationalNumber(input)
    }
  } else {
    if ((form.knowsAustralianAddress === 1 && form.contactCountry !== Country.Australia) || (form.knowsAustralianAddress === 0 && form.permanentCountry !== Country.Australia)) {
      return isValidInternationalNumber(input)
    }
  }
  return true
}

export const isValidAustralianHomeNumber = (input, context) => {
  let form = context.parent;

  if (input) {
    const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)

    if (!internationalOnshoreStudent) {
      if (
        (form.contactAddressSameAsPermanentAddress && form.permanentCountry === Country.Australia) ||
        (!form.contactAddressSameAsPermanentAddress && form.contactCountry === Country.Australia)
      ) {
        return isValidPhoneNumber(input)
      }
    } else {
      if ((form.knowsAustralianAddress === 1 && form.contactCountry === Country.Australia) || (form.knowsAustralianAddress === 0 && form.permanentCountry === Country.Australia)) {
        return isValidPhoneNumber(input)
      }
    }
  }

  return true
}

export const isValidInternationalHomeNumber = (input, context) => {
  let form = context.parent;

  if (input) {
    const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)

    if (!internationalOnshoreStudent) {
      if (
        (form.contactAddressSameAsPermanentAddress && form.permanentCountry !== Country.Australia) ||
        (!form.contactAddressSameAsPermanentAddress && form.contactCountry !== Country.Australia)
      ) {
        return isValidInternationalNumber(input)
      }
    } else {
      if ((form.knowsAustralianAddress === 1 && form.contactCountry !== Country.Australia) || (form.knowsAustralianAddress === 0 && form.permanentCountry !== Country.Australia)) {
        return isValidInternationalNumber(input)
      }
    }
  }

  return true
}

export const isValidInternationalNumber = input => {
  return isNotEmpty(input) && hasCharCountLessThanOrEqualTo(input, 20)
}

export const isValidPhoneNumber = input => {
  var phoneExpression = /^\({0,1}((0|\+61)(2|4|3|7|8)){0,1}\){0,1}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{2}(\ |-){0,1}[0-9]{1}(\ |-){0,1}[0-9]{3}$/
  return input && input.match(phoneExpression)
}

export const isValidPermanentCountry = (input, countryCodes = []) => {
  return isNotEmpty(input) && countryCodes.some(countryCode => countryCode.countryCode === input)
}

export const isValidCountryForPermanentResidencyType = (input, context) => {
  let form = context.parent;

  if (input) {
    const internationalStudent = isInternationalStudent(form.stuCitizenCd)
    if (internationalStudent) {
      return input !== Country.Australia
    } else {
      return input === Country.Australia
    }
  }
}

export const isValidPermanentAustralianSuburb = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry === Country.Australia) {
    return isNotEmpty(input) && doesNotContainNumerics(input) && hasCharCountLessThanOrEqualTo(input, 40)
  }
  return true
}

export const isValidPermanentInternationalSuburb = (input, context) => {
  let form = context.parent;

  if (form.permanentCountry !== Country.Australia) {
    return isNotEmpty(input) && hasCharCountLessThanOrEqualTo(input, 40)
  }
  return true
}

export const isValidContactCountry = (input, context, countryCodes = []) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  return isNotEmpty(input) && countryCodes.some(countryCode => countryCode.countryCode === input)
}

export const isValidCountryForContactResidencyType = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }
  if (input) {
    const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)
    const domesticStudent = isDomesticStudent(form.stuCitizenCd)

    if (internationalOnshoreStudent && form.knowsAustralianAddress === 1) {
      return input === Country.Australia
    } else if (domesticStudent && !form.contactAddressSameAsPermanentAddress) {
      return input === Country.Australia
    }
    return true
  }
}

export const isValidContactAddressLine1 = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  return isNotEmpty(input) && hasCharCountLessThanOrEqualTo(input, 50)
}

export const isValidContactAddressLine2 = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  return hasCharCountLessThanOrEqualTo(input, 50)
}

export const isValidContactAddressLine3 = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  return hasCharCountLessThanOrEqualTo(input, 50)
}

export const isValidContactAustraliaSuburb = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry === Country.Australia) {
    return isNotEmpty(input) && doesNotContainNumerics(input) && hasCharCountLessThanOrEqualTo(input, 40)
  }
  return true
}

export const isValidContactInternationalSuburb = (input, context) => {
  let form = context.parent;

  if (!shouldValidateContact(form)) {
    return true
  }

  if (form.contactCountry !== Country.Australia) {
    return isNotEmpty(input) && hasCharCountLessThanOrEqualTo(input, 40)
  }
  return true
}

const shouldValidateContact = form => {
  const internationalOnshoreStudent = isInternationalOnshoreStudent(form.stuCitizenCd, form.offshoreFlag)
  if ((internationalOnshoreStudent && form.knowsAustralianAddress === 1) || (!internationalOnshoreStudent && !form.contactAddressSameAsPermanentAddress)) {
    return true
  } else {
    return false
  }
}

const codePoints = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
//This can be any string of permitted characters

const numberOfValidInputCharacters = () => codePoints.length

const codePointFromCharacter = character => codePoints.indexOf(character)

const characterFromCodePoint = codePoint => codePoints.charAt(codePoint)

export const validateCheckCharacter = input => {
  if (input) {
    let factor = 1
    let sum = 0
    let n = 0
    let i = 0

    // Starting from the right, work leftwards
    // Now, the initial "factor" will always be "1"
    // since the last character is the check character.
    for (i = input.length - 1, sum = 0, n = numberOfValidInputCharacters(); i >= 0; i--) {
      let codePoint = codePointFromCharacter(input.charAt(i))
      let addend = factor * codePoint

      // Alternate the "factor" that each "codePoint" is multiplied by
      factor = factor == 2 ? 1 : 2

      // Sum the digits of the "addend" as expressed in base "n"
      addend = Math.floor(addend / n) + (addend % n)
      sum += addend
    }
    let remainder = sum % n
    return remainder == 0
  }
}
